import {
  DownOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, ConfigProvider, Form, Space } from 'antd';
import type { FormInstance } from 'antd/es/form';
import { createStyles } from 'antd-style';
import type { CSSProperties, ReactNode } from 'react';
import { useId, useState } from 'react';

const useStyles = createStyles(({ css, token }) => ({
  container: css`
    padding: 14px 16px;
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadiusLG}px;
    background: ${token.colorBgContainer};
  `,
  row: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
  `,
  field: css`
    flex: 0 1 auto;
    max-width: 100%;
    min-width: 0;
    .ant-form-item-label > label {
      color: ${token.colorTextSecondary};
      font-size: 13px;
    }
    @media (max-width: 767px) {
      flex: 1 1 100%;
      width: 100%;
      .ant-form-item-row {
        flex-wrap: nowrap;
      }
      .ant-form-item-label {
        flex: 0 0 80px;
        padding: 0;
        text-align: start;
      }
      .ant-form-item-control {
        flex: 1;
        min-width: 0;
      }
      .ant-input, .ant-input-affix-wrapper, .ant-select,
      .ant-picker, .ant-input-number {
        width: 100% !important;
        min-width: 0;
      }
    }
  `,
  actions: css`
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px 16px;
    margin-inline-start: auto;
    min-width: 0;
    @media (max-width: 767px) {
      justify-content: flex-start;
      margin-inline-start: 0;
      .ant-btn { min-height: 44px; }
    }
  `,
  overflow: css`
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${token.colorBorderSecondary};
    &[hidden] { display: none; }
  `,
}));

export interface CollapsibleFilterItem {
  key: string;
  label?: ReactNode;
  node: ReactNode;
  style?: CSSProperties;
}

interface CollapsibleFilterFormProps<T extends object> {
  form: FormInstance<T>;
  items: CollapsibleFilterItem[];
  defaultVisibleCount?: number;
  onFinish: (values: T) => void;
  onReset: () => void;
  primaryAction?: ReactNode;
  submitText?: string;
  style?: CSSProperties;
}

function CollapsibleFilterForm<T extends object>({
  form,
  items,
  defaultVisibleCount = 3,
  onFinish,
  onReset,
  primaryAction,
  submitText = '查询',
  style,
}: CollapsibleFilterFormProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const overflowId = useId();
  const { styles, cx } = useStyles();
  const shouldCollapse = items.length > defaultVisibleCount;

  const renderItem = (item: CollapsibleFilterItem) => (
    <div key={item.key} className={styles.field} style={item.style}>
      <Form.Item
        label={item.label}
        name={item.key as never}
        labelCol={{ flex: '0 0 auto' }}
        wrapperCol={{ flex: '1 1 auto' }}
      >
        {item.node}
      </Form.Item>
    </div>
  );

  return (
    <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 0 } } }}>
      <div className={styles.container} style={style}>
        <Form<T>
          form={form}
          onFinish={onFinish}
          layout="horizontal"
          autoComplete="off"
        >
          <div className={styles.row}>
            {items.slice(0, defaultVisibleCount).map(renderItem)}
            <div className={styles.actions}>
              <Space size={[8, 8]} wrap>
                <Button
                  htmlType="submit"
                  type="primary"
                  icon={<SearchOutlined />}
                >
                  {submitText}
                </Button>
                <Button onClick={onReset} icon={<ReloadOutlined />}>
                  重置
                </Button>
                {shouldCollapse && (
                  <Button
                    type="text"
                    onClick={() => setExpanded((value) => !value)}
                    aria-expanded={expanded}
                    aria-controls={overflowId}
                  >
                    {expanded ? '收起' : '更多筛选'}
                    <DownOutlined rotate={expanded ? 180 : 0} />
                  </Button>
                )}
              </Space>
              {primaryAction && (
                <Space size={[8, 8]} wrap>
                  {primaryAction}
                </Space>
              )}
            </div>
          </div>
          {shouldCollapse && (
            <div
              id={overflowId}
              className={cx(styles.row, styles.overflow)}
              hidden={!expanded}
            >
              {items.slice(defaultVisibleCount).map(renderItem)}
            </div>
          )}
        </Form>
      </div>
    </ConfigProvider>
  );
}

export default CollapsibleFilterForm;
